import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { FiX, FiZap, FiCopy } from 'react-icons/fi';
import type { Recipe, RecipeApp } from '../../../services/recipeService';

const STATUS_COLORS: Record<string, string> = {
  active:     '#10b981',
  draft:      '#6b7280',
  stopped:    '#6b7280',
  processing: '#f59e0b',
  failed:     '#ef4444',
};

function getStatusColor(s: string): string {
  return STATUS_COLORS[s] ?? '#6b7280';
}

const RecipeAppsStrip: React.FC<{ apps: RecipeApp[] }> = ({ apps }) => {
  const sorted  = [...apps].sort((a, b) => a.order - b.order);
  const visible = sorted.slice(0, 6);
  const overflow = sorted.length - 6;

  return (
    <div className="mws-modal__apps-strip">
      {visible.map((ra, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mws-modal__apps-arrow">›</span>}
          <div className="mws-modal__app-bubble">
            {ra.app?.icon?.url ? (
              <img src={ra.app.icon.url} alt={ra.app.name} />
            ) : (
              <FiZap size={11} />
            )}
          </div>
        </React.Fragment>
      ))}
      {overflow > 0 && (
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
          +{overflow}
        </span>
      )}
    </div>
  );
};

interface RecipeDetailModalProps {
  recipe: Recipe | null;
  isMine: boolean;
  onClose: () => void;
  actionSlot?: React.ReactNode;
}

const RecipeDetailModal: React.FC<RecipeDetailModalProps> = ({
  recipe,
  isMine,
  onClose,
  actionSlot,
}) => {
  if (!recipe) return null;

  const statusColor = getStatusColor(recipe.status);
  const steps       = recipe.recipe_apps?.length ?? 0;

  return ReactDOM.createPortal(
    <motion.div
      className="mws-modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="mws-modal"
        initial={{ opacity: 0, scale: 0.93, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 20 }}
        transition={{ duration: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mws-modal__head">
          <div
            className="mws-modal__icon"
            style={{ background: statusColor + '22', color: statusColor }}
          >
            <FiCopy size={16} />
          </div>
          <div className="mws-modal__title">{recipe.name}</div>
          <span
            className="mws-modal__badge"
            style={{
              background: statusColor + '1a',
              color: statusColor,
              border: `1px solid ${statusColor}44`,
            }}
          >
            {recipe.status}
          </span>
          <button className="mws-modal__close" onClick={onClose}>
            <FiX size={12} />
          </button>
        </div>

        {/* Body */}
        <div className="mws-modal__body">
          {/* Meta grid */}
          <div className="mws-modal__meta-grid">
            <div className="mws-modal__meta-item">
              <div className="mws-modal__meta-label">Steps</div>
              <div className="mws-modal__meta-value">{steps}</div>
            </div>
            <div className="mws-modal__meta-item">
              <div className="mws-modal__meta-label">Type</div>
              <div className="mws-modal__meta-value">
                {recipe.is_public ? 'Public' : 'Private'}
              </div>
            </div>
            {recipe.folder_name && (
              <div className="mws-modal__meta-item">
                <div className="mws-modal__meta-label">Folder</div>
                <div className="mws-modal__meta-value">{recipe.folder_name}</div>
              </div>
            )}
            <div className="mws-modal__meta-item">
              <div className="mws-modal__meta-label">Updated</div>
              <div className="mws-modal__meta-value">
                {new Date(recipe.updated_at).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Apps flow */}
          {steps > 0 && recipe.recipe_apps && (
            <div>
              <div className="mws-modal__section-label">Apps Flow</div>
              <RecipeAppsStrip apps={recipe.recipe_apps} />
            </div>
          )}

          {/* Description */}
          {recipe.description && (
            <div>
              <div className="mws-modal__section-label">Description</div>
              <p className="mws-modal__description">{recipe.description}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {actionSlot && (
          <div className="mws-modal__footer">{actionSlot}</div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default RecipeDetailModal;
